/**
 * Bringt MJML in die Form, die der Editor korrekt einliest.
 *
 * Der Fehler dahinter kostet eine ganze Vorlage, und man sieht ihm nichts an:
 *
 *   <mj-image src="…" />
 *   <mj-text>Sehr geehrter Herr …</mj-text>
 *
 * GrapesJS parst mit der HTML-Verarbeitung des Browsers. Die kennt nur eine
 * feste Liste von Elementen ohne Inhalt (`img`, `br`, `hr` …) — `mj-image`
 * steht nicht darauf. Ein `/>` am Ende ist für sie deshalb bedeutungslos: das
 * Element gilt als geöffnet, und ALLES, was folgt, wird sein Kind. Aus der
 * Vorlage oben wird:
 *
 *   <mj-image src="…"><mj-text>Sehr geehrter Herr …</mj-text></mj-image>
 *
 * Beim Speichern kommt genau das zurück, und der MJML-Compiler lehnt es ab
 * („mj-text cannot be used inside mj-image"). Sichtbar wird der Schaden erst
 * beim Prüfen — die Bearbeitungsfläche zeigt bis dahin nur ein Bild und
 * darunter nichts mehr.
 *
 * Diese Funktion schreibt selbstschließende MJML-Elemente deshalb in die
 * Paarform um, bevor der Editor sie zu sehen bekommt. Sie greift zentral: die
 * Vorlagengalerie, die CI-Bausteine, die Bildbibliothek und der KI-Assistent
 * erzeugen alle diese Schreibweise, und sie ist auch die in der
 * MJML-Dokumentation übliche.
 */

/**
 * MJML-Elemente ohne eigenen Inhalt. Nur diese dürfen umgeschrieben werden —
 * bei einem Container wäre die Paarform eine Bedeutungsänderung.
 */
const OHNE_INHALT = new Set([
  'mj-image',
  'mj-divider',
  'mj-spacer',
  'mj-raw',
  'mj-social-element',
  'mj-navbar-link',
  'mj-carousel-image',
  'mj-accordion-title',
  'mj-table',
]);

/**
 * Gesucht wird das Tagende zeichenweise, nicht per Regex.
 *
 * Ein Muster wie `<mj-image[^>]*?/>` scheitert an Attributwerten in beide
 * Richtungen, und beides ist im Alltag greifbar nah:
 *
 *   alt="Gewinn > Verlust"   → `[^>]` kommt am `>` nicht vorbei, das `/>`
 *                              wird nie gefunden, die Vorlage bleibt kaputt.
 *   alt="Termin 1 /> 2"      → das Muster endet MITTEN im Attributwert und
 *                              zerschneidet das Tag.
 *
 * Der Scanner in server/src/compiler/html-lesen.js löst dieselbe Aufgabe für
 * die Prüfung; hier steht sie noch einmal, weil Editor und Server getrennte
 * Pakete sind und diese Datei im Browser läuft.
 *
 * @param {string} mjml
 * @returns {string}
 */
export function mjmlNormalisieren(mjml) {
  if (!mjml) return mjml;

  let ergebnis = '';
  let i = 0;

  while (i < mjml.length) {
    const start = mjml.indexOf('<', i);
    if (start === -1) {
      ergebnis += mjml.slice(i);
      break;
    }

    const name = tagnameLesen(mjml, start);
    if (!name || !OHNE_INHALT.has(name)) {
      // Kein betroffenes Element: das `<` übernehmen und dahinter weitersuchen.
      ergebnis += mjml.slice(i, start + 1);
      i = start + 1;
      continue;
    }

    const ende = tagEndeFinden(mjml, start);
    if (ende === -1) {
      ergebnis += mjml.slice(i);
      break;
    }

    const roh = mjml.slice(start, ende + 1);
    ergebnis += mjml.slice(i, start);
    // Nur die selbstschließende Form umschreiben — `<mj-image …></mj-image>`
    // ist bereits richtig und bleibt unangetastet.
    ergebnis += /\/\s*>$/.test(roh)
      ? `${roh.slice(0, roh.lastIndexOf('/')).trimEnd()}></${name}>`
      : roh;
    i = ende + 1;
  }

  return ergebnis;
}

/**
 * Liest den Tagnamen an einer `<`-Position, in Kleinschreibung.
 *
 * @param {string} text
 * @param {number} start Index des `<`.
 * @returns {string|null}
 */
function tagnameLesen(text, start) {
  const treffer = /^<([a-zA-Z][-a-zA-Z0-9]*)/.exec(text.slice(start, start + 40));
  return treffer?.[1]?.toLowerCase() ?? null;
}

/**
 * Sucht das schließende `>` und überspringt dabei alles in Anführungszeichen.
 *
 * @param {string} text
 * @param {number} start Index des `<`.
 * @returns {number} Index des `>`, oder -1.
 */
function tagEndeFinden(text, start) {
  /** @type {'"' | "'" | null} */
  let anfuehrung = null;

  for (let i = start + 1; i < text.length; i++) {
    const zeichen = text[i];
    if (anfuehrung) {
      if (zeichen === anfuehrung) anfuehrung = null;
      continue;
    }
    if (zeichen === '"' || zeichen === "'") {
      anfuehrung = zeichen;
      continue;
    }
    if (zeichen === '>') return i;
  }
  return -1;
}
